import { Component, Injector, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ApiShivaService } from '@core/apis/api-shiva.service';
import { EQUIPMENT_MODEL_CATEGORY_OPTIONS, EQUIPMENT_MODEL_TYPES, EquipmentLocationEnum, OS_OPTIONS } from '@core/constants';
import { GenericDetailComponent } from '@core/globals/generic-detail/generic-detail.component';
import { WcmModalsService } from '@core/globals/wcm-modals/wcm-modals.service';
import { IEquipmentModel } from '@core/interfaces';

import { EquipmentModelsDetailStockModalComponent } from './equipment-models-detail-stock-modal.component';
import { EquipmentsModalComponent } from '@views/equipments/equipments-modal.component';
import { WaycomHttpErrorResponse } from '@core/services/waycom-http-error-response';


@Component({
  selector: 'app-equipment-models-detail',
  templateUrl: './equipment-models-detail.component.html',
  styles: []
})
export class EquipmentModelsDetailComponent extends GenericDetailComponent {
  @ViewChild('f', {static: true}) public detailForm: NgForm;

  private defaultBreadcrumbsData = [{label: 'Modèles d\'équipement', routerLink: '/equipment-models/list'}];
  // The viewName is used to build a key for the user preferences
  // Uncomment it if you want the last tab position to be saved in the user preferences
  // viewName = 'equipment-models';
  public equipment: {filters: {}, disabledColumns: {}, disabledButtons: {}};
  public categoryOptions = EQUIPMENT_MODEL_CATEGORY_OPTIONS;
  public types = EQUIPMENT_MODEL_TYPES;
  public listOS = OS_OPTIONS;
  private api: ApiShivaService['equipment_models'];
  public invalidWeight: boolean = false;

  constructor(
    private apiShiva: ApiShivaService,
    private wcmModalsService: WcmModalsService,
    private ngbModal: NgbModal,
    private router: Router,
    public injector: Injector
  ) {
    super(injector);
    this.breadcrumbsData = [...this.defaultBreadcrumbsData];
    // Default values for creation
    this.detail = {
      category: {label: 'zero-prov'}
    };
    // Api used for fetch, update and create
    this.api = this.apiShiva.equipment_models;

    // This enables the live update (websocket)
    this.liveUpdateChannel = 'equipmentModel';
  }

  public checkNumberDecimalDigits(value: number): boolean {
    // Checking that we don't have more than 2 decimal digits
    return String(value).split('.')[1]?.length > 2;
  }

  public onWeightChange(value: number): void {
    // Checking if the weight is valid
    this.invalidWeight = value === null || (value < 0.01 || value > 100.00 || this.checkNumberDecimalDigits(value));
  }

  public save(): void {
    if (!(this.detailForm && this.detailForm.valid)) {
      return;
    }
    this.loading = true;
    let promise: Promise<IEquipmentModel>;
    const payload = {...this.detail};
    if (this.detail.id) {
      promise = this.api.update(this.detail.id, payload);
    } else {
      promise = this.api.create(payload);
    }

    promise.then((res: IEquipmentModel) => {
      if (!this.detail.id) {
        // it was a creation
        this.pk = res.id;
        this.signalsService.broadcast('equipment-models:create', res.id);
        this._initTabs(res);
      }
      this.detail = res;
      this._updateBreadcrumbs();
      this.mode = 'normal';
      this.modeChanged.emit(this.mode);
      this.detailSaved.emit(this.detail);
    })
    .catch((error: unknown) => this._handleError(error))
    .finally(() => this.loading = false);
  }

  public openStockModal(): void {
    const modal = this.ngbModal.open(EquipmentModelsDetailStockModalComponent, {size: 'md'});
    modal.componentInstance.equipmentModel = this.detail;
    modal.result.then(() => {
      this._fetch();
    }, () => {});
  }

  public openCreateEquipmentModal(): void {
    const modal = this.ngbModal.open(EquipmentsModalComponent, {size: 'lg'});
    modal.componentInstance.contentType = 'detail';
    modal.componentInstance.defaults = {model: this.detail, location: EquipmentLocationEnum.Stock};

    modal.result.then(() => {
      this.signalsService.broadcast('equipments-list-refresh');
    }, () => {});
  }

  public confirmDelete(): void {
    const msgTitle = `Suppression du modèle d'équipement`;
    const msgBody = `Confirmez-vous la suppression de ce modèle d'équipement ?`;
    this.wcmModalsService.confirm(msgTitle, msgBody, 'Confirmer', 'Annuler')
      .then(() => {
        this._delete();
      }, () => {});
  }

  private _delete(): void {
    this.api.delete(this.detail.id, true)
      .then(() => this.router.navigateByUrl('/equipment-models/list'))
      .catch(err => Promise.reject(err));
  }

  protected _fetch(): void {
    this.loading = true;
    this.api.detail(this.pk)
      .then((res: IEquipmentModel) => {
        this.detail = res;
        this._updateBreadcrumbs();
        this._initTabs(res);
      })
      .catch(() => {})
      .finally(() => this.loading = false);
  }

  private _initTabs(detail: IEquipmentModel): void {
    // If any tab filter must be initialized, it's done here
    this.equipment = {
      filters: {
        model__id: detail.id,
      },
      disabledColumns: {
        model__name: true,
        action: true
      },
      disabledButtons: {
        exactSearch: true,
        assign: true,
        history: true,
      }
    };
  }

  private _updateBreadcrumbs(): void {
    this.breadcrumbsData = [...this.defaultBreadcrumbsData];
    if (this.detail.brand && this.detail.brand.id) {
      this.breadcrumbsData.push({
      label: this.detail.brand.name,
      routerLink: `/equipment-constructors/detail/${this.detail.brand.id}`,
      after: '>'}
      );
    }
    if (this.detail.id) {
      this.breadcrumbsData.push({
        label: this.detail.name,
        routerLink: `/equipment-models/detail/${this.detail.id}`,
        active: true
      });
    }
  }

  private _handleError(error: unknown): void {
    if (error instanceof WaycomHttpErrorResponse) {
      const errorMap: Record<string, string> = {
        BRANDED_NAME_ALREADY_EXISTS: 'La référence constructeur existe déjà.',
      };

      const errorMessage = errorMap[error.getFirstErrorMessage()];
      if (errorMessage) {
        this.toastr.error(errorMessage);
        return;
      }
    }
    Promise.reject(error);
  }
}
